import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Home, Users, CalendarDays, CheckSquare, ShieldCheck,
  PanelLeftClose, PanelLeftOpen, LogOut, ChevronDown,
  Bell, X, CheckCheck, Trash2, AlertTriangle,
  Briefcase, Target, Radio, LayoutDashboard,
  BookOpen, Building2, UserCheck, Menu,
  BarChart3, FileText, Server, CreditCard, Mail, TrendingUp, Search, Plus, CalendarCheck, HardDrive, Plug,
  Sun, Moon, Rows3, Rows4, Receipt, Clapperboard, Flame,
} from 'lucide-react';
import { ruoloLabel } from '../lib/ruoli';
import api from '../api';
import CommandPalette from './CommandPalette';
import QuickActions from './QuickActions';
import { Avatar } from './ui';
import { useIsMobile } from '../hooks/useResponsive';
import { useTheme } from '../hooks/useTheme';
import { useDensity } from '../hooks/useDensity';
import { useWorkspace } from '../hooks/useWorkspace';
import { WORKSPACE_INFO, WORKSPACE_NAV, PANORAMICA_NAV } from '../workspaces';
import WorkspaceSwitch from './WorkspaceSwitch';

const GRUPPI = [
  {
    label: 'Operativo',
    items: [
      { to: '/', icon: Home, label: 'Home' },
      { to: '/cockpit', icon: BarChart3, label: 'Cockpit', roles: ['admin'] },
      { to: '/oggi', icon: CalendarCheck, label: 'La mia giornata' },
      { to: '/clienti-attivi', icon: Users, label: 'Clienti attivi' },
      { to: '/leads', icon: Target, label: 'Leads', roles: ['admin','agente','direttore'] },
      { to: '/clienti', icon: UserCheck, label: 'Clienti' },
      { to: '/client-vault', icon: HardDrive, label: 'Client Vault' },
      { to: '/task', icon: CheckSquare, label: 'Task' },
      { to: '/calendario', icon: CalendarDays, label: 'Calendario' },
      { to: '/ped', icon: FileText, label: 'Piano Editoriale', match: ['/ped', '/ped-storie'] },
      { to: '/produzione', icon: Clapperboard, label: 'Produzione' },
      { to: '/pubblicazioni', icon: FileText, label: 'Pubblicazioni' },
      { to: '/trend', icon: Flame, label: 'Trend' },
      { to: '/guida', icon: BookOpen, label: 'Guida social' },
    ]
  },
  {
    label: 'Vendite B2B',
    items: [
      { to: '/b2b', icon: Building2, label: 'B2B Distribution' },
      { to: '/direttori', icon: Briefcase, label: 'Direttori' },
    ]
  },
  {
    label: 'Agenzia',
    items: [
      { to: '/pep-schede', icon: BookOpen, label: 'Schede Pep' },
      { to: '/eden-hq', icon: Building2, label: 'Eden HQ' },
      { to: '/agenti-vivi', icon: Radio, label: 'Agenti dal vivo' },
      { to: '/team', icon: Users, label: 'Team' },
    ]
  },
  {
    label: 'Marketing',
    items: [
      { to: '/analytics', icon: TrendingUp, label: 'Marketing Analytics', roles: ['admin','boss','social_manager'] },
      { to: '/cosa-funziona', icon: TrendingUp, label: 'Cosa funziona', roles: ['admin','boss','social_manager'] },
      { to: '/agenzia', icon: Building2, label: 'Vista Agenzia', roles: ['admin'] },
      { to: '/scadenzario', icon: CalendarDays, label: 'Scadenzario' },
    ]
  },
  {
    label: 'Sistema',
    items: [
      { to: '/fatturazione', icon: FileText, label: 'Fatturazione', roles: ['admin'] },
      { to: '/fattura-elettronica', icon: Receipt, label: 'Fattura Elettronica', roles: ['admin'] },
      { to: '/segnala', icon: AlertTriangle, label: '', labelFn: (u) => u?.ruolo === 'admin' ? 'Segnalazioni' : 'Segnala' },
      { to: '/servers', icon: Server, label: 'Servers', roles: ['admin'] },
      { to: '/status-mcp', icon: Plug, label: 'Status MCP', roles: ['admin'] },
      { to: '/abbonamenti', icon: CreditCard, label: 'Abbonamenti', roles: ['admin'] },
      { external: true, href: 'https://email.edenagents.it', icon: Mail, label: 'Email', roles: ['admin'] },
      { to: '/admin', icon: ShieldCheck, label: 'Admin', roles: ['admin'] },
      { to: '/audit', icon: ShieldCheck, label: 'Audit log', roles: ['admin'] },
    ]
  }
];

// Titoli per le pagine che non sono voci di menu (dettagli, sottopagine).
const TITOLI_EXTRA = [
  ['/ped/contenuto', 'Contenuto', 'Piano Editoriale'],
  ['/ped-storie', 'Storie', 'Piano Editoriale'],
  ['/guida-trend', 'Guida ai trend', 'Trend'],
  ['/campagne', 'Campagne', 'Piano Editoriale'],
  ['/clienti/', 'Scheda cliente', 'Clienti'],
  ['/studio', 'Il mio lavoro', null],
  ['/panoramica', 'Panoramica', null],
  ['/onboarding', 'Onboarding cliente', 'Clienti'],
  ['/gamification', 'Gamification', 'Agenzia'],
];

const isVisible = (item, ruolo) => !item.roles || item.roles.includes(ruolo);

function pathMatches(pathname, to, match) {
  if (!to) return false;
  if (to === '/') return pathname === '/';
  if (pathname === to || pathname.startsWith(to + '/')) return true;
  return Array.isArray(match) && match.some(p => pathname === p || pathname.startsWith(p + '/'));
}

// Titolo + sezione della pagina corrente (per la barra in alto)
function usePageInfo(pathname, focus, workspace, user) {
  return useMemo(() => {
    for (const [prefix, title, section] of TITOLI_EXTRA) {
      if (pathname.startsWith(prefix)) return { title, section };
    }
    if (pathname === '/') return { title: focus ? 'Il mio lavoro' : 'Home', section: focus ? WORKSPACE_INFO[workspace].label : null };
    // Dentro uno spazio di lavoro, le sue pagine portano il suo nome (non il gruppo del menu completo).
    if (focus) {
      const it = WORKSPACE_NAV[workspace].find(i => i.to !== '/' && pathMatches(pathname, i.to, i.match));
      if (it) return { title: it.label, section: WORKSPACE_INFO[workspace].label };
    }
    for (const g of GRUPPI) {
      for (const it of g.items) {
        if (it.to && it.to !== '/' && pathMatches(pathname, it.to)) {
          return { title: it.labelFn ? it.labelFn(user) : it.label, section: g.label };
        }
      }
    }
    return { title: 'EdenFlow', section: null };
  }, [pathname, focus, workspace, user]);
}

const TIPO_COLOR = {
  task:   { bg:'rgba(0,212,255,0.1)',   color:'var(--cyan)',   dot:'#00d4ff' },
  utente: { bg:'rgba(34,197,94,0.1)',   color:'var(--green)',  dot:'#22c55e' },
  info:   { bg:'rgba(148,163,184,0.1)', color:'var(--text-muted)', dot:'#94a3b8' },
  errore: { bg:'rgba(239,68,68,0.1)',   color:'var(--red)',    dot:'#ef4444' },
};

function fmtAgo(ts) {
  const diff = (Date.now() - new Date(ts)) / 1000;
  if (diff < 60) return 'ora';
  if (diff < 3600) return `${Math.floor(diff/60)}min fa`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h fa`;
  return `${Math.floor(diff/86400)}g fa`;
}

function NotifichePanel({ notifiche, onSegnaLetta, onSegnaLetteTutte, onEliminaLette, onClose, onNavigate, isMobile }) {
  const nonLette = notifiche.filter(n => !n.letta).length;
  return (
    <div style={{
      position:'absolute',
      top:'calc(100% + 10px)',
      right: isMobile ? -8 : 0,
      width: isMobile ? 'calc(100vw - 24px)' : 360,
      maxWidth: 380,
      maxHeight:'70vh',
      background:'var(--bg2)', border:'1px solid var(--border)',
      borderRadius:14, boxShadow:'0 20px 60px rgba(0,0,0,0.5)',
      display:'flex', flexDirection:'column', zIndex:1000,
      overflow:'hidden',
    }}>
      <div style={{ padding:'14px 16px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <Bell size={14} color="var(--cyan)"/>
          <span style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>Notifiche</span>
          {nonLette > 0 && (
            <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:999, background:'var(--cyan)', color:'var(--on-accent)' }}>
              {nonLette}
            </span>
          )}
        </div>
        <div style={{ display:'flex', gap:6 }}>
          {nonLette > 0 && (
            <button onClick={onSegnaLetteTutte} title="Segna tutte come lette"
              style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-dim)', padding:8, borderRadius:4, display:'flex', minWidth:32, minHeight:32, alignItems:'center', justifyContent:'center' }}>
              <CheckCheck size={14}/>
            </button>
          )}
          <button onClick={onEliminaLette} title="Elimina lette"
            style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-dim)', padding:8, borderRadius:4, display:'flex', minWidth:32, minHeight:32, alignItems:'center', justifyContent:'center' }}>
            <Trash2 size={13}/>
          </button>
          <button onClick={onClose}
            style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-dim)', padding:8, borderRadius:4, display:'flex', minWidth:32, minHeight:32, alignItems:'center', justifyContent:'center' }}>
            <X size={14}/>
          </button>
        </div>
      </div>
      <div style={{ overflowY:'auto', flex:1 }}>
        {notifiche.length === 0 ? (
          <div style={{ padding:'40px 20px', textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>
            Nessuna notifica
          </div>
        ) : notifiche.map(n => {
          const tc = TIPO_COLOR[n.tipo] || TIPO_COLOR.info;
          return (
            <div key={n.id}
              onClick={() => { if (!n.letta) onSegnaLetta(n.id); if (n.link) onNavigate(n.link); }}
              style={{
                padding:'14px 16px', borderBottom:'1px solid var(--border)',
                background: n.letta ? 'transparent' : tc.bg,
                cursor: n.link || !n.letta ? 'pointer' : 'default',
                display:'flex', gap:10, alignItems:'flex-start',
                touchAction:'manipulation',
              }}
            >
              <div style={{ paddingTop:5, flexShrink:0 }}>
                {!n.letta
                  ? <div style={{ width:7, height:7, borderRadius:'50%', background:tc.dot }}/>
                  : <div style={{ width:7, height:7 }}/>
                }
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight: n.letta?400:600, color:'var(--text)', lineHeight:1.4, display:'flex', alignItems:'center', gap:5 }}>
                  <span style={{ flex:1 }}>{n.titolo}</span>
                  {n.link && <span style={{ fontSize:10, color: tc.color, flexShrink:0 }}>Apri →</span>}
                </div>
                {n.messaggio && (
                  <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:2, lineHeight:1.4 }}>
                    {n.messaggio}
                  </div>
                )}
                <div style={{ fontSize:11, color:'var(--text-dim)', marginTop:4 }}>
                  {fmtAgo(n.creato_il)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Voce di navigazione ─────────────────────────────────────────────── */
function NavItem({ to, href, external, icon: Icon, label, collapsed, onClick, match }) {
  const { pathname } = useLocation();
  const cls = (on) => `eden-nav-item${on ? ' is-active' : ''}${collapsed ? ' is-collapsed' : ''}`;
  const inner = (
    <>
      <Icon size={16} strokeWidth={1.9} />
      {!collapsed && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>}
    </>
  );
  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick} className={cls(false)} title={collapsed ? label : undefined}>
        {inner}
      </a>
    );
  }
  const extra = Array.isArray(match) && match.some(p => pathname === p || pathname.startsWith(p + '/'));
  return (
    <NavLink to={to} end={to === '/'} onClick={onClick} className={({ isActive }) => cls(isActive || extra)}
      title={collapsed ? label : undefined} aria-label={label}>
      {inner}
    </NavLink>
  );
}

/* ── Gruppi richiudibili (stato ricordato) ────────────────────────────── */
function useOpenGroups() {
  const [open, setOpen] = useState(() => {
    try { return JSON.parse(localStorage.getItem('eden-nav-groups')) || { Operativo: true }; } catch { return { Operativo: true }; }
  });
  const toggle = useCallback((label) => setOpen(o => {
    const n = { ...o, [label]: !o[label] };
    try { localStorage.setItem('eden-nav-groups', JSON.stringify(n)); } catch {}
    return n;
  }), []);
  return [open, toggle];
}

function NavGroups({ ruolo, user, collapsed, focus, onNavClick }) {
  const { pathname } = useLocation();
  const [open, toggle] = useOpenGroups();
  return GRUPPI.map(gruppo => {
    const items = gruppo.items
      .filter(it => isVisible(it, ruolo))
      .map(it => (focus && it.to === '/' ? { ...it, ...PANORAMICA_NAV } : it));
    if (!items.length) return null;
    // il gruppo che contiene la pagina corrente è sempre aperto
    const hasActive = items.some(it => pathMatches(pathname, it.to, it.match));
    const isOpen = collapsed || hasActive || !!open[gruppo.label];
    return (
      <div key={gruppo.label}>
        {collapsed ? <div style={{ height: 1, background: 'var(--border)', margin: '10px 8px' }} /> : (
          <button className="eden-nav-group" onClick={() => toggle(gruppo.label)} aria-expanded={isOpen}>
            {gruppo.label}
            <ChevronDown size={13} style={{ transform: isOpen ? 'none' : 'rotate(-90deg)', transition: 'transform 0.18s var(--ease)' }} />
          </button>
        )}
        {isOpen && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {items.map(item => (
              <NavItem key={item.to || item.href} to={item.to} href={item.href} external={item.external}
                icon={item.icon} label={item.labelFn ? item.labelFn(user) : item.label}
                match={item.match} collapsed={collapsed} onClick={onNavClick} />
            ))}
          </div>
        )}
      </div>
    );
  });
}

/* ── Menu utente (tema, densità, esci) ────────────────────────────────── */
function UserMenu({ user, collapsed, onLogout, theme, onToggleTheme, compact, onToggleDensity }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const nome = `${user?.nome || ''} ${user?.cognome || ''}`.trim();
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {open && (
        <div className="eden-menu eden-toast-in" style={{ position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, width: 220, zIndex: 50 }}>
          <button className="eden-menu-item" onClick={onToggleTheme}>
            {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}{theme === 'dark' ? 'Tema chiaro' : 'Tema scuro'}
          </button>
          <button className="eden-menu-item" onClick={onToggleDensity}>
            {compact ? <Rows3 size={15} /> : <Rows4 size={15} />}{compact ? 'Layout comodo' : 'Layout compatto'}
          </button>
          <div style={{ height: 1, background: 'var(--border)', margin: '4px 6px' }} />
          <button className="eden-menu-item" onClick={onLogout} style={{ color: 'var(--red)' }}>
            <LogOut size={15} style={{ color: 'var(--red)' }} />Esci
          </button>
        </div>
      )}
      <button onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open} title={collapsed ? nome : undefined}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10, minHeight: 0,
          justifyContent: collapsed ? 'center' : 'flex-start',
          padding: collapsed ? 6 : '8px 8px', borderRadius: 10, border: 'none', cursor: 'pointer',
          background: open ? 'var(--surface-h)' : 'transparent', color: 'var(--text)', font: 'inherit', textAlign: 'left',
        }}
        onMouseEnter={e => { if (!open) e.currentTarget.style.background = 'var(--surface-h)'; }}
        onMouseLeave={e => { if (!open) e.currentTarget.style.background = 'transparent'; }}>
        <Avatar name={nome || '?'} size={30} />
        {!collapsed && (
          <>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nome}</span>
              <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-dim)', }}>{ruoloLabel(user?.ruolo)}</span>
            </span>
            <ChevronDown size={14} color="var(--text-dim)" style={{ transform: 'rotate(180deg)' }} />
          </>
        )}
      </button>
    </div>
  );
}

/* ── Sidebar desktop ──────────────────────────────────────────────────── */
function Sidebar({ collapsed, onCollapse, ruolo, user, ...menuProps }) {
  const { workspace, focus } = useWorkspace();
  const [altroAperto, setAltroAperto] = useState(() => {
    try { return localStorage.getItem('eden-nav-altro') === '1'; } catch { return false; }
  });
  const toggleAltro = () => {
    const nv = !altroAperto;
    setAltroAperto(nv);
    try { localStorage.setItem('eden-nav-altro', nv ? '1' : '0'); } catch {}
  };
  return (
    <aside className="eden-sidebar" style={{
      width: collapsed ? 64 : 244, flexShrink: 0, position: 'sticky', top: 0, height: '100dvh',
      display: 'flex', flexDirection: 'column', transition: 'width 0.2s var(--ease)', overflow: 'hidden', zIndex: 150,
    }}>
      {/* Logo */}
      <div style={{ height: 'var(--topbar-h)', display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between', padding: collapsed ? 0 : '0 10px 0 16px', flexShrink: 0 }}>
        {!collapsed && (
          <Link to="/" aria-label="EdenFlow — Home" style={{ display: 'flex' }}>
            <img src="/edenflow_logo_white.png" alt="EdenFlow" className="brand-logo" style={{ height: 26, width: 'auto', display: 'block' }} />
          </Link>
        )}
        <button className="eden-icon-btn" onClick={onCollapse} title={collapsed ? 'Espandi menu' : 'Comprimi menu'} aria-label={collapsed ? 'Espandi menu' : 'Comprimi menu'}>
          {collapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
        </button>
      </div>

      {/* Spazio di lavoro */}
      {!collapsed && (
        <div style={{ padding: '2px 12px 6px' }}>
          <WorkspaceSwitch size="block" />
        </div>
      )}

      {/* Navigazione */}
      <nav aria-label="Navigazione principale" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: collapsed ? '6px 12px 12px' : '4px 12px 12px' }}>
        {focus && (
          <>
            {!collapsed && <div className="eden-nav-group" style={{ cursor: 'default', color: 'var(--cyan)' }}>{WORKSPACE_INFO[workspace].label}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {WORKSPACE_NAV[workspace].filter(it => isVisible(it, ruolo)).map(item => (
                <NavItem key={item.to} to={item.to} icon={item.icon} label={item.label} match={item.match} collapsed={collapsed} />
              ))}
            </div>
            <button className="eden-nav-group" onClick={toggleAltro} aria-expanded={altroAperto}
              style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 14, justifyContent: collapsed ? 'center' : 'space-between' }}
              title="Tutte le sezioni">
              {!collapsed && 'Tutte le sezioni'}
              <ChevronDown size={13} style={{ transform: altroAperto ? 'none' : 'rotate(-90deg)', transition: 'transform 0.18s var(--ease)' }} />
            </button>
          </>
        )}
        {(!focus || altroAperto) && <NavGroups ruolo={ruolo} user={user} collapsed={collapsed} focus={focus} />}
      </nav>

      {/* Utente */}
      <div style={{ padding: 10, borderTop: '1px solid var(--border)', flexShrink: 0 }}>
        <UserMenu user={user} collapsed={collapsed} {...menuProps} />
      </div>
    </aside>
  );
}

/* ── Menu mobile (foglio dal basso con tutte le sezioni) ──────────────── */
function MobileMenu({ open, onClose, ruolo, user, onLogout, theme, onToggleTheme }) {
  const { pathname } = useLocation();
  const { workspace, focus } = useWorkspace();
  useEffect(() => { if (open) onClose(); }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;
  const nome = `${user?.nome || ''} ${user?.cognome || ''}`.trim();
  const sezioni = [
    ...(focus ? [{ label: WORKSPACE_INFO[workspace].label, items: WORKSPACE_NAV[workspace] }] : []),
    ...GRUPPI.map(g => ({ ...g, items: g.items.map(it => (focus && it.to === '/' ? { ...it, ...PANORAMICA_NAV } : it)) })),
  ];
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 400 }} role="dialog" aria-modal="true" aria-label="Menu">
      <div className="eden-backdrop" onClick={onClose} />
      <div className="eden-sheet">
        <div className="eden-sheet-handle" />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <Avatar name={nome || '?'} size={40} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 650, color: 'var(--text)' }}>{nome}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-dim)', textTransform: 'capitalize' }}>{user?.ruolo}</div>
          </div>
          <button className="eden-icon-btn" onClick={onClose} aria-label="Chiudi menu"><X size={18} /></button>
        </div>
        <div style={{ marginBottom: 16 }}><WorkspaceSwitch size="block" /></div>
        {sezioni.map(g => {
          const items = g.items.filter(it => isVisible(it, ruolo));
          if (!items.length) return null;
          return (
            <div key={g.label} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-dim)', margin: '0 2px 8px' }}>{g.label}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8 }}>
                {items.map(it => {
                  const Icon = it.icon;
                  const label = it.labelFn ? it.labelFn(user) : it.label;
                  if (it.external) {
                    return <a key={it.href} href={it.href} target="_blank" rel="noopener noreferrer" className="eden-tile"><Icon size={20} />{label}</a>;
                  }
                  const on = pathMatches(pathname, it.to, it.match);
                  return <Link key={it.to} to={it.to} className={`eden-tile${on ? ' is-active' : ''}`}><Icon size={20} />{label}</Link>;
                })}
              </div>
            </div>
          );
        })}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 4 }}>
          <button onClick={onToggleTheme} className="eden-btn-ghost" style={{ justifyContent: 'center' }}>
            {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}{theme === 'dark' ? 'Tema chiaro' : 'Tema scuro'}
          </button>
          <button onClick={onLogout} className="eden-btn-ghost" style={{ justifyContent: 'center', color: 'var(--red)' }}>
            <LogOut size={15} />Esci
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Tab bar mobile ───────────────────────────────────────────────────── */
function TabBar({ onMenu, menuOpen }) {
  const { pathname } = useLocation();
  const { focus } = useWorkspace();
  const tabs = [
    { to: '/', icon: focus ? LayoutDashboard : Home, label: focus ? 'Lavoro' : 'Home' },
    { to: '/ped', icon: FileText, label: 'PED', match: ['/ped-storie', '/campagne'] },
    null, // pulsante Crea
    { to: '/task', icon: CheckSquare, label: 'Task' },
  ];
  return (
    <nav className="eden-tabbar" aria-label="Navigazione rapida">
      {tabs.map((t, i) => t === null ? (
        <div key="fab">
          <button className="eden-tab-fab" aria-label="Crea"
            onClick={() => window.dispatchEvent(new CustomEvent('eden-open-quickadd'))}>
            <Plus size={22} strokeWidth={2.4} />
          </button>
        </div>
      ) : (
        <Link key={t.to} to={t.to} className={`eden-tab${!menuOpen && pathMatches(pathname, t.to, t.match) ? ' is-active' : ''}`}>
          <t.icon size={21} strokeWidth={1.9} />{t.label}
        </Link>
      ))}
      <button className={`eden-tab${menuOpen ? ' is-active' : ''}`} onClick={onMenu} aria-label="Menu">
        <Menu size={21} strokeWidth={1.9} />Menu
      </button>
    </nav>
  );
}

/* ── Layout ───────────────────────────────────────────────────────────── */
export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggle: toggleTheme } = useTheme();
  const { compact, toggle: toggleDensity } = useDensity();
  const { workspace, focus } = useWorkspace();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('eden-sidebar-collapsed') === '1'; } catch { return false; }
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifiche, setNotifiche] = useState([]);
  const [showNotifiche, setShowNotifiche] = useState(false);
  const panelRef = useRef(null);
  const isMobile = useIsMobile();
  const page = usePageInfo(location.pathname, focus, workspace, user);

  const toggleCollapsed = () => setCollapsed(c => {
    try { localStorage.setItem('eden-sidebar-collapsed', c ? '0' : '1'); } catch {}
    return !c;
  });

  // Titolo della scheda del browser = pagina corrente
  useEffect(() => { document.title = page.title === 'EdenFlow' ? 'EdenFlow' : `${page.title} · EdenFlow`; }, [page.title]);

  // Torna in cima quando si cambia pagina
  useEffect(() => { window.scrollTo(0, 0); setShowNotifiche(false); }, [location.pathname]);

  // Blocca lo scroll della pagina quando il menu mobile è aperto
  useEffect(() => {
    document.body.style.overflow = isMobile && menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobile, menuOpen]);

  const nonLette = notifiche.filter(n => !n.letta).length;

  const fetchNotifiche = useCallback(async () => {
    try {
      const res = await api.get('/notifiche?tutte=true');
      setNotifiche(Array.isArray(res.data) ? res.data : []);
    } catch {}
  }, []);

  useEffect(() => {
    if (!user) return;
    // Genera notifiche da eventi reali (task scadenti, abbonamenti, playbook), poi carica
    api.post('/notifiche/genera').catch(() => {}).finally(fetchNotifiche);
    const id = setInterval(fetchNotifiche, 20000);
    return () => clearInterval(id);
  }, [user, fetchNotifiche]);

  useEffect(() => {
    if (!showNotifiche) return;
    const handler = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setShowNotifiche(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showNotifiche]);

  const segnaLetta = async (id) => {
    await api.patch(`/notifiche/${id}/letta`);
    setNotifiche(ns => ns.map(n => n.id === id ? { ...n, letta: true } : n));
  };
  const segnaLetteTutte = async () => {
    await api.post('/notifiche/segna_tutte');
    setNotifiche(ns => ns.map(n => ({ ...n, letta: true })));
  };
  const eliminaLette = async () => {
    await api.delete('/notifiche/elimina_lette');
    setNotifiche(ns => ns.filter(n => !n.letta));
  };

  const ruolo = user?.ruolo;
  const handleLogout = () => { logout(); navigate('/login'); };
  const openPalette = () => window.dispatchEvent(new CustomEvent('eden-open-palette'));
  const menuProps = { onLogout: handleLogout, theme, onToggleTheme: toggleTheme, compact, onToggleDensity: toggleDensity };

  return (
    <div style={{ display: 'flex', minHeight: '100dvh', background: 'var(--bg)' }}>
      {!isMobile && (
        <Sidebar collapsed={collapsed} onCollapse={toggleCollapsed} ruolo={ruolo} user={user} {...menuProps} />
      )}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* ── Barra superiore ── */}
        <header className="eden-topbar" style={{ position: 'sticky', top: 0, zIndex: 100, flexShrink: 0 }}>
          <div style={{ height: 'var(--topbar-h)', display: 'flex', alignItems: 'center', gap: 10, padding: isMobile ? '0 8px 0 16px' : '0 20px 0 24px' }}>
            {/* Dove sono */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              {!isMobile && page.section && (
                <>
                  <span style={{ fontSize: 13, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>{page.section}</span>
                  <span style={{ color: 'var(--text-dim)', opacity: 0.6 }}>/</span>
                </>
              )}
              {isMobile ? (
                // su telefono il titolo è già nella pagina: qui solo il marchio (tap = Home)
                <Link to="/" aria-label="EdenFlow — Home" style={{ display: 'flex' }}>
                  <img src="/edenflow_logo_white.png" alt="EdenFlow" className="brand-logo" style={{ height: 24, width: 'auto', display: 'block' }} />
                </Link>
              ) : (
                <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {page.title}
                </span>
              )}
            </div>

            {/* Ricerca globale (⌘K) */}
            {isMobile ? (
              <button className="eden-icon-btn" onClick={openPalette} aria-label="Cerca"><Search size={19} /></button>
            ) : (
              <button className="eden-search-trigger" onClick={openPalette} aria-label="Cerca" style={{ width: 300 }}>
                <Search size={15} />
                <span style={{ flex: 1 }}>Cerca clienti, contenuti, task…</span>
                <kbd className="eden-kbd">⌘K</kbd>
              </button>
            )}

            {/* Crea (su mobile è il pulsante centrale della tab bar) */}
            {!isMobile && (
              <button onClick={() => window.dispatchEvent(new CustomEvent('eden-open-quickadd'))} title="Crea (n)"
                className="eden-btn-primary" style={{ height: 36, padding: '0 14px', fontSize: 13, borderRadius: 10 }}>
                <Plus size={16} strokeWidth={2.4} />Crea
              </button>
            )}

            {/* Notifiche */}
            <div style={{ position: 'relative' }} ref={panelRef}>
              <button className={`eden-icon-btn${showNotifiche ? ' is-on' : ''}`} onClick={() => setShowNotifiche(s => !s)}
                aria-label={nonLette ? `Notifiche (${nonLette} non lette)` : 'Notifiche'}>
                <Bell size={isMobile ? 19 : 17} />
                {nonLette > 0 && (
                  <span style={{ position: 'absolute', top: 4, right: 3, fontSize: 9.5, fontWeight: 700, minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 999, background: 'var(--red)', color: '#fff', padding: '0 4px', border: '2px solid var(--bg)' }}>
                    {nonLette > 99 ? '99+' : nonLette}
                  </span>
                )}
              </button>
              {showNotifiche && (
                <NotifichePanel
                  notifiche={notifiche}
                  onSegnaLetta={segnaLetta}
                  onSegnaLetteTutte={segnaLetteTutte}
                  onEliminaLette={eliminaLette}
                  onClose={() => setShowNotifiche(false)}
                  onNavigate={(link) => { setShowNotifiche(false); navigate(link); }}
                  isMobile={isMobile}
                />
              )}
            </div>
          </div>
        </header>

        <main style={{ flex: 1, minWidth: 0, overflowX: 'clip', paddingBottom: isMobile ? 'calc(var(--tabbar-h) + env(safe-area-inset-bottom) + 12px)' : 0 }}>
          <div key={location.pathname} className="eden-route">{children}</div>
        </main>
      </div>

      {isMobile && (
        <>
          <TabBar onMenu={() => setMenuOpen(o => !o)} menuOpen={menuOpen} />
          <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} ruolo={ruolo} user={user}
            onLogout={handleLogout} theme={theme} onToggleTheme={toggleTheme} />
        </>
      )}
      <CommandPalette />
      <QuickActions />
    </div>
  );
}
